import { useMemo } from "react";
import InventoryToolbar from "./InventoryToolbar";
import GroupByMenu from "./GroupByMenu";
import WarehouseFilter from "./WarehouseFilter";
import OptionFilter from "./OptionFilter";
import type { StockMatrixRow, WarehouseColumn } from "./StockMatrixGrid";
import type { GroupByMode } from "../lib/groupProducts";
import { groupOptions, type ProductFilters } from "../lib/productFilters";

interface Props {
  warehouses: WarehouseColumn[];
  // Every row the server sent — whether "Unassigned" is offered comes from
  // here, not from the filtered rows, so an option never vanishes once picked.
  products: StockMatrixRow[];
  // The product group tree in display order, parents before children.
  productGroups: { id: string; path: string; depth: number }[];
  hiddenWarehouseIds: Set<string>;
  onHiddenWarehouseIdsChange: (next: Set<string>) => void;
  filters: ProductFilters;
  onFiltersChange: (next: ProductFilters) => void;
  groupBy: GroupByMode;
  onGroupByChange: (next: GroupByMode) => void;
  // Filters before Warehouse (the Show filter).
  leading?: React.ReactNode;
  // Buttons after Group by (the all-markets page's Expand all / Collapse all).
  actions?: React.ReactNode;
}

// The bar above the by-warehouse stock matrix: the free-text search, the Show
// filter, warehouse columns toggle, product group row filter, and Group by. All
// selections are session-only page state; the server always sends everything.
export default function StockMatrixToolbar({
  warehouses,
  products,
  productGroups,
  hiddenWarehouseIds,
  onHiddenWarehouseIdsChange,
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  leading,
  actions,
}: Props) {
  const options = useMemo(() => groupOptions(productGroups, products), [productGroups, products]);

  return (
    <InventoryToolbar
      searchQuery={filters.query}
      onSearchChange={(query) => onFiltersChange({ ...filters, query })}
    >
      {leading}
      <WarehouseFilter
        warehouses={warehouses}
        hiddenIds={hiddenWarehouseIds}
        onChange={onHiddenWarehouseIdsChange}
      />
      <OptionFilter
        label="Product group"
        options={options}
        selected={filters.groups}
        onChange={(groups) => onFiltersChange({ ...filters, groups })}
        searchable
      />
      <GroupByMenu value={groupBy} onChange={onGroupByChange} />
      {actions}
    </InventoryToolbar>
  );
}
